/**
 * ADMIN DASHBOARD — operational control centre for the sole platform admin.
 *
 * ---------------------------------------------------------------------------
 * FREEZE ROOT CAUSE (fixed in this file + lib/adminQueue.ts)
 * ---------------------------------------------------------------------------
 * 1. EAGER IMPORT GRAPH. This page previously imported ~45 admin panels with
 *    static `import` statements. Every one of them (and their icon/chart
 *    dependencies) was parsed and evaluated before the first pixel of the
 *    dashboard could paint, on the main thread, even though only ONE tab is
 *    ever visible. That is the multi-second "white / unresponsive" window.
 *    -> Every panel is now React.lazy, so a tab downloads and evaluates only
 *       its own chunk, and the page shell paints immediately.
 *
 * 2. UNBOUNDED OVERVIEW QUERIES. The overview effect fired five `select('*')`
 *    reads with no column list, no limit, no cancellation and no error
 *    handling, each calling setState independently — five renders of the whole
 *    page, growing linearly with the marketplace, and state writes landing
 *    after the operator had already switched tabs.
 *    -> One Promise.all, explicit columns, hard limits, a single commit, and
 *       an `alive` guard.
 *
 * 3. NO TIMEOUT / NO CONCURRENCY LIMIT ON EDGE CALLS. Diagnostic-heavy tabs
 *    mount 4-6 panels that each auto-run an edge function (live DNS lookups,
 *    SMTP seed tests, the ops sweep, integrity scoring). `functions.invoke()`
 *    has no deadline, so one non-answering function leaves a spinner forever;
 *    and six of them saturate the browser's ~6-connection-per-origin pool, so
 *    every ordinary PostgREST read queues behind them and the whole dashboard
 *    stops responding.
 *    -> All admin edge calls now run through the request budget in
 *       lib/adminQueue.ts (max 3 in flight, 20s hard deadline) via invokeEdge.
 *
 * ---------------------------------------------------------------------------
 * DATA VISIBILITY
 * ---------------------------------------------------------------------------
 * The Projects tab exposes full homeowner contact details, purchase history,
 * credit ledger, membership, payment references, CRM links, security events
 * and internal notes. All of it is read through the EXISTING administrator RLS
 * policies — no policy was widened. Public pages read mkt_listings_public,
 * which carries no contact name, phone, email or street address, so none of
 * this can reach browse pages, the sitemap, schema output or a search index.
 */
import React, { Suspense, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Users, FileText, Ticket, DollarSign, TrendingUp, MapPin, LayoutDashboard, Inbox, Images,
  ShieldAlert, CreditCard, Shield, SlidersHorizontal, Webhook, MailCheck, Star, ClipboardCheck,
  Share2, FolderSearch, Loader2, LifeBuoy, Globe, MessageSquare,
} from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import AdminAccessDenied from '@/components/AdminAccessDenied';
import { mfa, currentAal } from '@/lib/accountSecurity';

import {
  money,
  tradeLabel,
  LEAD_PRICE,
  PLAN_CODES,
  PLAN_PRICES,
  PLAN_INCLUDED_LEADS,
  PLAN_TERM_MONTHS,
  planBillingSuffix,
} from '@/data/constants';

// ---------------------------------------------------------------------------
// LAZY PANELS. One chunk per panel: a tab costs only what that tab shows.
// ---------------------------------------------------------------------------
const MfaEnrollment = React.lazy(() => import('@/components/MfaEnrollment'));
const AdminProjects = React.lazy(() => import('@/components/AdminProjects'));
const AdminInbox = React.lazy(() => import('@/components/AdminInbox'));
const AdminTestimonials = React.lazy(() => import('@/components/AdminTestimonials'));
const TestimonialRequests = React.lazy(() => import('@/components/admin/TestimonialRequests'));
const AdminPayments = React.lazy(() => import('@/components/AdminPayments'));
const AdminDisputes = React.lazy(() => import('@/components/AdminDisputes'));
const AdminPaymentBranding = React.lazy(() => import('@/components/AdminPaymentBranding'));
const AdminIncidents = React.lazy(() => import('@/components/AdminIncidents'));
const AdminWebhookQuarantine = React.lazy(() => import('@/components/AdminWebhookQuarantine'));
const AdminGhlProducts = React.lazy(() => import('@/components/AdminGhlProducts'));
const AdminWebhookSetup = React.lazy(() => import('@/components/AdminWebhookSetup'));
const AdminActivationChain = React.lazy(() => import('@/components/AdminActivationChain'));
const AdminPaymentRecovery = React.lazy(() => import('@/components/AdminPaymentRecovery'));
// P1 PAYMENT RECONCILIATION: one state per successful payment + secure claim / admin match.
const AdminPaymentReconciliation = React.lazy(() => import('@/components/AdminPaymentReconciliation'));
// HOSTED $29 CHECKOUTS WITH NO PAYMENT RECEIVED: confirm a GHL-verified payment in one step.
const AdminUnconfirmedCheckouts = React.lazy(() => import('@/components/AdminUnconfirmedCheckouts'));
// ONE-TIME CHECKOUT RECOVERY EMAILS (lead + membership, sent by ghl-payment-sync after 30 min unpaid).
const AdminCheckoutRecovery = React.lazy(() => import('@/components/AdminCheckoutRecovery'));
// INTERNAL SALES ALERTS: one email to the sales team per confirmed payment (sales-payment-notify).
const AdminSalesAlerts = React.lazy(() => import('@/components/AdminSalesAlerts'));
// TRANSACTION ALERTS: queue / runs / delivery for transaction-alerts (rodartebrand@gmail.com).
const AdminTransactionAlerts = React.lazy(() => import('@/components/AdminTransactionAlerts'));
const AdminLeadCredits = React.lazy(() => import('@/components/AdminLeadCredits'));
const AdminConcurrencyProof = React.lazy(() => import('@/components/AdminConcurrencyProof'));
const AdminGoDaddyGuide = React.lazy(() => import('@/components/AdminGoDaddyGuide'));
const AdminDnsAlignment = React.lazy(() => import('@/components/AdminDnsAlignment'));
const AdminEmailAudit = React.lazy(() => import('@/components/AdminEmailAudit'));
const AdminInboxTest = React.lazy(() => import('@/components/AdminInboxTest'));
const AdminEmailHealth = React.lazy(() => import('@/components/AdminEmailHealth'));
const AdminReviewQueue = React.lazy(() => import('@/components/AdminReviewQueue'));
const AdminAccountIntegrity = React.lazy(() => import('@/components/AdminAccountIntegrity'));
const AdminOpsMonitor = React.lazy(() => import('@/components/AdminOpsMonitor'));
const AdminSecurity = React.lazy(() => import('@/components/AdminSecurity'));
const BrowseRegressionPanel = React.lazy(() => import('@/components/BrowseRegressionPanel'));
const AdminListingSettings = React.lazy(() => import('@/components/AdminListingSettings'));
const AdminExpiryQueue = React.lazy(() => import('@/components/AdminExpiryQueue'));
const ListingsModeration = React.lazy(() => import('@/components/AdminModeration'));
const RoleAudit = React.lazy(() =>
  import('@/components/AdminModeration').then((m) => ({ default: m.RoleAudit }))
);
const AdminCoverAudit = React.lazy(() => import('@/components/AdminCoverAudit'));
const AdminAdministrators = React.lazy(() => import('@/components/AdminAdministrators'));
const AdminCreateSubcontractor = React.lazy(() => import('@/components/AdminCreateSubcontractor'));
const AdminReleaseChecks = React.lazy(() => import('@/components/AdminReleaseChecks'));
const AdminGhlChecklist = React.lazy(() => import('@/components/AdminGhlChecklist'));
const AdminRevenue = React.lazy(() => import('@/components/AdminRevenue'));
const AdminBillingVerification = React.lazy(() => import('@/components/AdminBillingVerification'));
const AdminPiiAccess = React.lazy(() => import('@/components/AdminPiiAccess'));
const AdminHomeStats = React.lazy(() => import('@/components/AdminHomeStats'));
const AdminListingRisk = React.lazy(() => import('@/components/AdminListingRisk'));
const AdminHomeownerTrust = React.lazy(() => import('@/components/admin/AdminHomeownerTrust'));
// SUPPORT CASES: one-screen customer view + grant credits + onboarding resend.
const AdminCustomers = React.lazy(() => import('@/components/AdminCustomers'));
// $29 LEAD PURCHASE HEALTH: routing (card form vs hosted), provider health, paid-vs-credited, alerts.
const AdminLeadPurchaseHealth = React.lazy(() => import('@/components/AdminLeadPurchaseHealth'));
// HANDS-OFF GO-LIVE: 6 production-proven steps (GHL scopes → sync → webhook → test buy → auto credit → unlock).
const AdminGoLiveChecklist = React.lazy(() => import('@/components/AdminGoLiveChecklist'));
// MEMBERSHIP COUPONS: create codes, push to GHL, pre-applied links, redemption log.
const AdminCoupons = React.lazy(() => import('@/components/AdminCoupons'));
// PREMIUM FREE WEBSITE: fulfilment queue.
const AdminWebsiteRequests = React.lazy(() => import('@/components/AdminWebsiteRequests'));
// A2P 10DLC SMS CONSENT: register + CSV export, legacy review, GHL retry queue + contact-id backfill.
const AdminSmsConsent = React.lazy(() => import('@/components/admin/AdminSmsConsent'));
// Carrier STOP / DND sync with GoHighLevel (ghl-sms-optout-sync).
const SmsOptoutSyncPanel = React.lazy(() => import('@/components/admin/SmsOptoutSync'));
// SAVED LEADS REPORT: saves per lead, most saved leads, saves per user.
const AdminSavedLeads = React.lazy(() => import('@/components/admin/AdminSavedLeads'));
// PAYMENT DENYLIST (2026-10-07b): known card-testing emails / card fingerprints / IP hashes, with audit trail.
const AdminPaymentDenylist = React.lazy(() => import('@/components/admin/AdminPaymentDenylist'));
// PROJECT OUTCOMES (2026-10-08): homeowner closing flow -> hired jobs, contractor win rates.
const AdminProjectOutcomes = React.lazy(() => import('@/components/AdminProjectOutcomes'));
// GEO ZIP CLEANUP (2026-10-09): correct placeholder ZIPs, add PO-box ZIPs, email owners.
const AdminGeoCleanup = React.lazy(() => import('@/components/admin/AdminGeoCleanup'));



const PanelFallback: React.FC = () => (
  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
    <Loader2 className="h-4 w-4 animate-spin" /> Loading panel…
  </div>
);

/** Every tab body is wrapped so a slow chunk can never block the shell. */
const Panel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={className || 'mt-8'}>
    <Suspense fallback={<PanelFallback />}>{children}</Suspense>
  </div>
);

interface Row { [k: string]: any }

type TabId =
  | 'overview' | 'customers' | 'websites' | 'projects' | 'inbox' | 'reviews' | 'revenue' | 'payments' | 'webhook' | 'txnalerts' | 'credits'
  | 'email' | 'security' | 'settings' | 'listings' | 'roles' | 'release' | 'crm' | 'sms';

const TAB_IDS: TabId[] = ['overview', 'customers', 'websites', 'projects', 'inbox', 'reviews', 'revenue', 'payments', 'webhook', 'txnalerts', 'credits', 'email', 'security', 'settings', 'listings', 'roles', 'release', 'crm', 'sms'];

const Admin: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, loading } = useAuth();
  // Deep link: /en/admin?tab=sms opens the SMS consent console directly.
  const [tab, setTab] = useState<TabId>(() => {
    const q = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null;
    return q && (TAB_IDS as string[]).includes(q) ? (q as TabId) : 'overview';
  });

  const [users, setUsers] = useState<Row[]>([]);
  const [listings, setListings] = useState<Row[]>([]);
  const [purchases, setPurchases] = useState<Row[]>([]);
  const [subs, setSubs] = useState<Row[]>([]);
  const [activity, setActivity] = useState<Row[]>([]);
  const [statsError, setStatsError] = useState('');

  // OVERVIEW DATA. One round trip, explicit columns, hard limits, cancellable,
  // committed in a single render instead of five.
  useEffect(() => {
    if (!profile?.is_admin) return;
    let alive = true;

    (async () => {
      try {
        const [u, l, p, s, a] = await Promise.all([
          supabase
            .from('mkt_profiles')
            .select('id,email,user_type,first_name,last_name,company_name,preferred_language,status')
            .order('created_at', { ascending: false })
            .limit(200),
          supabase.from('mkt_listings').select('id,trade,city,state,source_type').limit(1000),
          supabase.from('mkt_lead_purchases').select('id,amount,stage').limit(1000),
          supabase.from('mkt_subscriptions').select('id,amount').eq('status', 'active').limit(500),
          supabase
            .from('mkt_activity')
            .select('id,action,detail,created_at')
            .order('created_at', { ascending: false })
            .limit(25),
        ]);
        if (!alive) return;
        setUsers(u.data || []);
        setListings(l.data || []);
        setPurchases(p.data || []);
        setSubs(s.data || []);
        setActivity(a.data || []);
        setStatsError(u.error || l.error ? 'Some overview data could not be loaded.' : '');
      } catch {
        if (alive) setStatsError('Overview data could not be loaded.');
      }
    })();

    return () => {
      alive = false;
    };
  }, [profile?.is_admin]);

  // Administrator MFA gate. The authoritative check is server-side (every admin
  // edge function refuses an AAL1 token once enforcement is on); this only stops
  // the dashboard from rendering administrative data in that state.
  const [mfaGate, setMfaGate] = useState<{ enforced: boolean; aal: string; enrolled: boolean } | null>(null);
  useEffect(() => {
    if (!profile?.is_admin) return;
    let alive = true;
    void (async () => {
      try {
        const [{ data }, aal] = await Promise.all([mfa.status(), currentAal()]);
        if (alive) setMfaGate({ enforced: !!data?.enforced, aal, enrolled: !!data?.mfa_enrolled });
      } catch {
        if (alive) setMfaGate({ enforced: false, aal: 'aal1', enrolled: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, [profile?.is_admin]);

  if (loading) return <div className="py-24 text-center text-slate-500">{t('common.loading')}</div>;
  // Keep the deep link (e.g. ?tab=customers&user=… from a transaction alert) through sign-in.
  if (!profile)
    return (
      <Navigate
        to={`${path('/login')}?next=${encodeURIComponent(
          typeof window !== 'undefined' ? window.location.pathname + window.location.search : path('/admin')
        )}`}
        replace
      />
    );
  if (!profile.is_admin) return <AdminAccessDenied />;

  if (mfaGate?.enforced && mfaGate.aal !== 'aal2') {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <Seo title={t('admin.title')} description={t('admin.title')} noindex />
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6">
          <h1 className="text-xl font-extrabold text-amber-900">Second factor required</h1>
          <p className="mt-2 text-sm text-amber-900">
            Administrator access requires multi-factor authentication. Complete the challenge below — administrative APIs
            reject this session until it reaches AAL2.
          </p>
        </div>
        <Panel className="mt-6">
          <MfaEnrollment />
        </Panel>
      </div>
    );
  }

  const byType = (ty: string) => users.filter((u) => u.user_type === ty).length;
  const won = purchases.filter((p) => p.stage === 'won').length;
  const conversion = purchases.length ? Math.round((won / purchases.length) * 100) : 0;


  const tradeCounts: Record<string, number> = {};
  listings.forEach((l) => (tradeCounts[l.trade] = (tradeCounts[l.trade] || 0) + 1));
  const topTrades = Object.entries(tradeCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const cityCounts: Record<string, number> = {};
  listings.forEach((l) => {
    const k = `${l.city}, ${l.state}`;
    cityCounts[k] = (cityCounts[k] || 0) + 1;
  });
  const topCities = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const toggleStatus = async (u: Row) => {
    const next = u.status === 'suspended' ? 'approved' : 'suspended';
    await supabase.from('mkt_profiles').update({ status: next }).eq('id', u.id);
    setUsers((p) => p.map((x) => (x.id === u.id ? { ...x, status: next } : x)));
  };

  const setUserLang = async (u: Row, l: string) => {
    await supabase.from('mkt_profiles').update({ preferred_language: l }).eq('id', u.id);
    setUsers((p) => p.map((x) => (x.id === u.id ? { ...x, preferred_language: l } : x)));
  };

  // KPIs. NOTE: no money figure is computed here any more. The overview used
  // to sum mkt_lead_purchases.amount + mkt_subscriptions.amount in the browser,
  // which double counted every membership lead (already recognised as
  // subscription revenue when the credit was granted) and never subtracted a
  // refund. Money now lives ONLY on the Revenue tab, recognised from settled
  // payment rows. These counters are volume, not currency.
  const kpis = [
    { icon: Users, label: t('admin.homeowners'), value: byType('homeowner') },
    { icon: Users, label: t('admin.gcs'), value: byType('gc') },
    { icon: Users, label: t('admin.subs'), value: byType('subcontractor') },
    { icon: FileText, label: t('admin.projects'), value: listings.filter((l) => l.source_type === 'homeowner').length },
    { icon: FileText, label: t('admin.workOrders'), value: listings.filter((l) => l.source_type === 'gc').length },
    { icon: Ticket, label: t('admin.leadsSold'), value: purchases.length },
    { icon: Users, label: lang === 'es' ? 'Miembros activos' : 'Active members', value: subs.length },
    {
      icon: DollarSign,
      label: lang === 'es' ? 'Ingresos' : 'Revenue',
      value: lang === 'es' ? 'Ver pestaña' : 'See tab',
    },
  ];

  const tabs: Array<{ id: TabId; label: string; icon: React.ElementType }> = [
    { id: 'overview', label: t('admin.tabOverview'), icon: LayoutDashboard },
    // SUPPORT: resolve a customer case (history + grant credits + resend onboarding).
    { id: 'customers', label: lang === 'es' ? 'Clientes' : 'Customers', icon: LifeBuoy },
    // PREMIUM FULFILMENT: free-website requests (requested -> in progress -> delivered).
    { id: 'websites', label: lang === 'es' ? 'Sitios web' : 'Websites', icon: Globe },
    // OPERATIONAL CONSOLE: drill into any project for the complete record.
    { id: 'projects', label: lang === 'es' ? 'Proyectos' : 'Projects', icon: FolderSearch },
    { id: 'inbox', label: t('admin.tabInbox'), icon: Inbox },
    { id: 'reviews', label: t('testi.adminTitle'), icon: Star },
    { id: 'revenue', label: lang === 'es' ? 'Ingresos' : 'Revenue', icon: TrendingUp },
    { id: 'payments', label: t('admin.tabPayments'), icon: CreditCard },
    { id: 'webhook', label: lang === 'es' ? 'Webhook y recuperación' : 'Webhook & recovery', icon: Webhook },
    { id: 'txnalerts', label: lang === 'es' ? 'Alertas de transacciones' : 'Transaction alerts', icon: MailCheck },
    { id: 'credits', label: lang === 'es' ? 'Créditos y cupos' : 'Credits & capacity', icon: Ticket },
    { id: 'email', label: lang === 'es' ? 'Entrega de correo' : 'Email delivery', icon: MailCheck },
    { id: 'security', label: t('admin.tabSecurity'), icon: Shield },
    { id: 'settings', label: t('admin.tabSettings'), icon: SlidersHorizontal },
    { id: 'listings', label: t('admin.tabListings'), icon: Images },
    { id: 'roles', label: t('admin.tabRoles'), icon: ShieldAlert },
    { id: 'release', label: lang === 'es' ? 'Pruebas de release' : 'Release checks', icon: ClipboardCheck },
    { id: 'crm', label: lang === 'es' ? 'CRM y GoHighLevel' : 'CRM & GoHighLevel', icon: Share2 },
    { id: 'sms', label: lang === 'es' ? 'Consentimiento SMS' : 'SMS consent', icon: MessageSquare },
  ];


  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Seo title={t('admin.title')} description={t('admin.title')} noindex />
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('admin.title')}</h1>

      <div className="mt-6 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors ${
              tab === tb.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tb.icon className="h-4 w-4" /> {tb.label}
          </button>
        ))}
      </div>

      {tab === 'customers' ? (
        <Panel>
          <AdminCustomers />
        </Panel>
      ) : tab === 'websites' ? (
        <Panel>
          <AdminWebsiteRequests />
        </Panel>
      ) : tab === 'projects' ? (
        <Panel>
          {/* Homeowner closing outcomes: hired jobs, win rates, reported job value. */}
          <AdminProjectOutcomes />
          <AdminProjects />
        </Panel>
      ) : tab === 'release' ? (
        <Panel>
          <AdminReleaseChecks />
        </Panel>
      ) : tab === 'inbox' ? (
        <Panel>
          <AdminInbox />
        </Panel>
      ) : tab === 'reviews' ? (
        <Panel>
          <TestimonialRequests />
          <AdminTestimonials />
        </Panel>
      ) : tab === 'payments' ? (
        <Panel className="mt-8 space-y-8">
          {/* FIRST: is the purchase provably hands-off end-to-end? Then live routing/health. */}
          <AdminGoLiveChecklist />
          <AdminLeadPurchaseHealth />
          {/* Membership discount codes (Basic / Pro / Premium). */}
          <AdminCoupons />
          <AdminPayments />
          <AdminDisputes />
          <AdminPaymentBranding />
        </Panel>
      ) : tab === 'txnalerts' ? (
        <Panel>
          <AdminTransactionAlerts />
        </Panel>
      ) : tab === 'webhook' ? (
        <Panel className="mt-8 space-y-8">
          {/* P1: every successful payment in exactly one state (fulfilled / matching / failed / refunded / disputed). */}
          <AdminPaymentReconciliation />
          <AdminIncidents />
          <AdminWebhookQuarantine />
          <AdminGhlProducts />
          <AdminWebhookSetup />
          <AdminActivationChain />
          <AdminUnconfirmedCheckouts />
          <AdminCheckoutRecovery />
          <AdminSalesAlerts />
          <AdminPaymentRecovery />
        </Panel>
      ) : tab === 'credits' ? (
        <Panel>
          <AdminLeadCredits />
          <AdminConcurrencyProof />
          {/* Demand signal: saves per lead, most saved, saves per user. */}
          <AdminSavedLeads />
        </Panel>
      ) : tab === 'email' ? (
        <Panel>
          <AdminGoDaddyGuide />
          <AdminDnsAlignment />
          <AdminEmailAudit />
          <AdminInboxTest />
          <AdminEmailHealth />
        </Panel>
      ) : tab === 'security' ? (
        <Panel>
          {/* Known card-testing identities refused before any PaymentIntent / hosted hand-off / registration. */}
          <div className="mb-8">
            <AdminPaymentDenylist />
          </div>
          <MfaEnrollment />
          <div className="mt-8">
            <AdminReviewQueue />
          </div>
          <div className="mt-8">
            <AdminAccountIntegrity />
          </div>
          <div className="mt-8">
            <AdminOpsMonitor />
          </div>
          <div className="mt-8">
            <AdminSecurity />
          </div>
          <div className="mt-8">
            {/* Who opened private contact data, and by what right. */}
            <AdminPiiAccess />

          </div>
          <BrowseRegressionPanel />
        </Panel>
      ) : tab === 'settings' ? (
        <Panel className="mt-8 space-y-8">
          <AdminListingSettings />
          <AdminExpiryQueue />
          {/* Proof that the four home page counters are live production data,
              with the cache window and the recorded fixture test. */}
          <AdminHomeStats />
        </Panel>

      ) : tab === 'listings' ? (
        <Panel>
          {/* Fraud/risk review queue + 0/3..3/3 capacity with purchasers and method. */}
          {/* Homeowner trust score, verification status, fraud + duplicate-risk indicators (2026-10-10). */}
          <AdminHomeownerTrust />
          <AdminListingRisk />
          <ListingsModeration />
          <AdminCoverAudit />
          {/* Geo cleanup: placeholder / unreferenced ZIPs, owner confirmation emails. */}
          <AdminGeoCleanup />
        </Panel>
      ) : tab === 'roles' ? (
        <Panel>
          <AdminAdministrators />
          <RoleAudit />
          <AdminCreateSubcontractor />
        </Panel>
      ) : tab === 'revenue' ? (
        <Panel className="mt-8 space-y-8">
          <AdminRevenue />
          {/* What the PROVIDER says about recurrence — and any subscription
              being billed there with no membership here. */}
          <AdminBillingVerification />
        </Panel>

      ) : tab === 'sms' ? (
        <Panel>
          {/* A2P 10DLC: consent register + CSV, legacy review, GHL retry queue + backfill. */}
          <AdminSmsConsent />
          {/* Carrier STOP / DND sync with GoHighLevel (bidirectional verification). */}
          <div className="mt-8"><SmsOptoutSyncPanel /></div>
        </Panel>
      ) : tab === 'crm' ? (

        <Panel>
          <AdminGhlChecklist />
        </Panel>
      ) : (
        <div>
          {statsError ? (
            <p className="mt-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {statsError}
            </p>
          ) : null}

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((k) => (
              <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-5">
                <k.icon className="h-5 w-5 text-orange-500" />
                <p className="mt-3 text-2xl font-extrabold text-slate-900">{k.value}</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('admin.conversion')}</h2>
              <p className="mt-3 text-4xl font-extrabold text-emerald-600">{conversion}%</p>
              <div className="mt-3 h-2 w-full rounded-full bg-slate-100">
                <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${conversion}%` }} />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('admin.topTrades')}</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {topTrades.map(([tr, c]) => (
                  <li key={tr} className="flex justify-between">
                    <span className="text-slate-600">{tradeLabel(tr, lang)}</span>
                    <span className="font-bold text-slate-900">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="flex items-center gap-1.5 text-sm font-extrabold uppercase tracking-wide text-slate-900">
                <MapPin className="h-4 w-4 text-slate-400" /> {t('admin.geo')}
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                {topCities.map(([c, n]) => (
                  <li key={c} className="flex justify-between">
                    <span className="text-slate-600">{c}</span>
                    <span className="font-bold text-slate-900">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <h2 className="mt-12 text-xl font-extrabold text-slate-900">{t('admin.manageUsers')}</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{t('common.name')}</th>
                  <th className="px-4 py-3">{t('common.email')}</th>
                  <th className="px-4 py-3">{t('common.type')}</th>
                  <th className="px-4 py-3">{t('auth.preferredLanguage')}</th>
                  <th className="px-4 py-3">{t('common.status')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      {u.company_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3 text-slate-600">{u.user_type}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.preferred_language}
                        onChange={(e) => setUserLang(u, e.target.value)}
                        className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-bold"
                      >
                        <option value="en">EN</option>
                        <option value="es">ES</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                          u.status === 'suspended' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {u.status === 'suspended' ? t('admin.suspended') : t('admin.approved')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleStatus(u)}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        {u.status === 'suspended' ? t('admin.approve') : t('admin.suspend')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="mt-12 text-xl font-extrabold text-slate-900">{t('admin.managePricing')}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('pricing.payPerLead')}</p>
              <p className="mt-2 text-3xl font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
              <p className="mt-1 text-xs text-slate-500">{t('pricing.fixedLeadNote')}</p>
            </div>
            {PLAN_CODES.map((code) => (
              <div key={code} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{code}</p>
                <p className="mt-2 text-3xl font-extrabold text-slate-900">
                  {money(PLAN_PRICES[code])}
                  <span className="text-sm font-semibold text-slate-400">{planBillingSuffix(code, lang)}</span>
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {PLAN_INCLUDED_LEADS[code]} {t('dash.includedLeads')}
                  {PLAN_TERM_MONTHS[code] > 1 ? ` · ${PLAN_TERM_MONTHS[code]} mo` : ''}
                </p>
              </div>
            ))}
          </div>

          <h2 className="mt-12 text-xl font-extrabold text-slate-900">{t('admin.activity')}</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {activity.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-500">{t('common.none')}</p>
            ) : (
              activity.map((a) => (
                <div
                  key={a.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 text-sm last:border-0"
                >
                  <span className="font-semibold text-slate-800">{a.action}</span>
                  <span className="text-slate-500">{a.detail}</span>
                  <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
